import { cn } from "@/lib/utils";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import type { ReactNode } from "react";

type FinancialMetricTileProps = {
  readonly icon: React.ComponentType<{ className?: string }>;
  readonly label: string;
  readonly year: number | null;
  readonly value: ReactNode;
  /** A line under the value saying how to read it. */
  readonly hint?: string;
  /** The change from the previous year, in percent: shown when it rounds to non-zero. */
  readonly yoyPercentage?: number | null;
};

/**
 * The grid the financial tiles flow in: the summary and the ratios share one,
 * so a short row of the first is filled by the second.
 */
export function FinancialMetricGrid({
  children,
}: {
  readonly children: ReactNode;
}) {
  return (
    <div className="@container">
      <div className="grid grid-cols-1 @md:grid-cols-2 @2xl:grid-cols-4 gap-4">
        {children}
      </div>
    </div>
  );
}

/** One figure of the company's accounts, as a tile of a `FinancialMetricGrid`. */
export default function FinancialMetricTile({
  icon: Icon,
  label,
  year,
  value,
  hint,
  yoyPercentage,
}: FinancialMetricTileProps) {
  const roundedYoy = yoyPercentage != null ? Math.round(yoyPercentage) : 0;

  return (
    <div className="flex items-center gap-3 px-5 py-4 rounded-xl border border-border bg-background">
      <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <div className="flex flex-col min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-medium truncate">
            {label}
            {year != null && <span className="ml-1 opacity-60">{year}</span>}
          </span>
          {roundedYoy !== 0 && (
            <span
              className={cn(
                "flex items-center gap-0.5 text-xs font-medium rounded-full px-2 py-0.5 shrink-0",
                roundedYoy > 0
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                  : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
              )}
            >
              {roundedYoy > 0 ? (
                <ArrowUpIcon className="w-3 h-3" />
              ) : (
                <ArrowDownIcon className="w-3 h-3" />
              )}
              {roundedYoy > 0 ? "+" : ""}
              {roundedYoy} %
            </span>
          )}
        </div>
        <span className="text-lg font-bold leading-tight tabular-nums">
          {value}
        </span>
        {hint && (
          <span className="text-xs text-muted-foreground truncate">{hint}</span>
        )}
      </div>
    </div>
  );
}
